
import { useState, FormEvent } from 'react';

const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    projectType: '',
    yardage: '',
    hoseLength: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('address', formData.address);
      formBody.append('projectType', formData.projectType);
      formBody.append('yardage', formData.yardage);
      formBody.append('hoseLength', formData.hoseLength);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6hcd1d4ieqv1pmm5760', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          address: '',
          projectType: '',
          yardage: '',
          hoseLength: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="quote-form" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-black mb-6">
            Get Your Free Quote
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Fill out the form below and we'll get back to you with a detailed quote for your concrete pumping project.
          </p>
        </div>

        <form
          id="concrete-pumping-quote-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-bold text-black mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-bold text-black mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="(828) 555-0123"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-bold text-black mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
              placeholder="john@example.com"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="address" className="block text-sm font-bold text-black mb-2">
              Job Address *
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
              placeholder="123 Main Street, Otto, NC 28763"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label htmlFor="projectType" className="block text-sm font-bold text-black mb-2">
                Project Type *
              </label>
              <select
                id="projectType"
                name="projectType"
                value={formData.projectType}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-sm cursor-pointer"
              >
                <option value="">Select Type</option>
                <option value="Poured Wall">Poured Wall</option>
                <option value="Block Fill">Block Fill</option>
                <option value="Flatwork">Flatwork</option>
                <option value="Footers">Footers</option>
                <option value="Foundation Pour">Foundation Pour</option>
                <option value="Pool">Pool</option>
                <option value="Columns">Columns</option>
                <option value="Retaining Wall">Retaining Wall</option>
                <option value="Commercial Pour">Commercial Pour</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="yardage" className="block text-sm font-bold text-black mb-2">
                Estimated Yardage *
              </label>
              <input
                type="text"
                id="yardage"
                name="yardage"
                value={formData.yardage}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="e.g., 25 yards"
              />
            </div>

            <div>
              <label htmlFor="hoseLength" className="block text-sm font-bold text-black mb-2">
                Hose Length Needed *
              </label>
              <input
                type="text"
                id="hoseLength"
                name="hoseLength"
                value={formData.hoseLength}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="e.g., 150 feet"
              />
            </div>
          </div>

          <div className="mb-8">
            <label htmlFor="message" className="block text-sm font-bold text-black mb-2">
              Additional Details
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              maxLength={500}
              rows={5}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-sm resize-none"
              placeholder="Tell us more about your project..."
            ></textarea>
            <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border-2 border-green-500 rounded-lg">
              <p className="text-green-700 font-bold text-center">
                Thank you! We'll contact you soon with your quote.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-500 rounded-lg">
              <p className="text-red-700 font-bold text-center">
                Something went wrong. Please try again or call us directly.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-yellow-500 text-black py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? 'SUBMITTING...' : 'GET MY QUOTE'}
          </button>
        </form>
      </div>
    </section>
  );
};

export default QuoteForm;
